import { getBestOpenGraphImage, getSocialImageUrl } from './imageUtils';

/**
 * Converts a relative or absolute image URL to a proper absolute URL for Open Graph
 * @param {string} imageUrl - Image URL (can be relative or absolute)
 * @returns {string} Absolute image URL
 */
const ensureAbsoluteImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return 'https://jobsopportunity.in/images/og-default.jpg';
  }

  // If already absolute URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // Convert relative URL to absolute
  const baseUrl = 'https://jobsopportunity.in';
  if (imageUrl.startsWith('/')) {
    return `${baseUrl}${imageUrl}`;
  }

  return `${baseUrl}/${imageUrl}`;
};

/**
 * Updates all SEO meta tags for the current page
 * @param {Object} seo - SEO data from backend API
 * @param {Object} contentData - Optional content data (job/blog) to extract images if SEO data lacks them
 */
export const updatePageSEO = (seo, contentData = null) => {
  if (!seo) return;

  // Update document title
  document.title = seo.title || 'Jobs Opportunity';

  // Update meta description
  updateMetaTag('name', 'description', seo.description || '');

  // Update canonical URL
  const canonicalUrl = seo.canonical || (typeof window !== 'undefined' ? window.location.href : 'https://jobsopportunity.in');
  updateLinkTag('canonical', canonicalUrl);

  // Determine the best image URL
  let ogImageUrl = seo.og?.image;
  let twitterImageUrl = seo.twitter?.image;

  // If no image in SEO data, try to extract from content data
  if ((!ogImageUrl || !twitterImageUrl) && contentData) {
    const contentType = seo.og?.type === 'article' ? 'blog' : 'job';
    const extractedImage = getBestOpenGraphImage(contentData, contentType);
    if (extractedImage) {
      ogImageUrl = ogImageUrl || extractedImage;
      twitterImageUrl = twitterImageUrl || extractedImage;
    }
  }

  // Ensure images are absolute URLs
  ogImageUrl = ensureAbsoluteImageUrl(ogImageUrl);
  twitterImageUrl = ensureAbsoluteImageUrl(twitterImageUrl);

  // Update Open Graph tags
  updateMetaTag('property', 'og:title', seo.og?.title || seo.title || 'Jobs Opportunity');
  updateMetaTag('property', 'og:description', seo.og?.description || seo.description || '');
  updateMetaTag('property', 'og:url', seo.og?.url || canonicalUrl);
  updateMetaTag('property', 'og:type', seo.og?.type || 'website');
  updateMetaTag('property', 'og:site_name', seo.og?.site_name || 'Jobs Opportunity');
  updateMetaTag('property', 'og:image', ogImageUrl);
  updateMetaTag('property', 'og:image:width', seo.og?.image_width || '1200');
  updateMetaTag('property', 'og:image:height', seo.og?.image_height || '630');
  updateMetaTag('property', 'og:image:alt', seo.og?.image_alt || seo.og?.title || seo.title || 'Jobs Opportunity');

  // Update Twitter Card tags
  updateMetaTag('name', 'twitter:card', seo.twitter?.card || 'summary_large_image');
  updateMetaTag('name', 'twitter:title', seo.twitter?.title || seo.og?.title || seo.title || 'Jobs Opportunity');
  updateMetaTag('name', 'twitter:description', seo.twitter?.description || seo.og?.description || seo.description || '');
  updateMetaTag('name', 'twitter:image', twitterImageUrl);
  updateMetaTag('name', 'twitter:image:alt', seo.twitter?.image_alt || seo.og?.title || seo.title || 'Jobs Opportunity');

  // Add article-specific meta tags if type is article
  if (seo.og?.type === 'article') {
    if (seo.og?.published_time) {
      updateMetaTag('property', 'article:published_time', seo.og.published_time);
    }
    if (seo.og?.modified_time) {
      updateMetaTag('property', 'article:modified_time', seo.og.modified_time);
    }
    if (seo.og?.author) {
      updateMetaTag('property', 'article:author', seo.og.author);
    }
  }

  // Add structured data (JSON-LD)
  if (seo.schema) {
    addStructuredData(seo.schema);
  }

  console.log('✅ SEO meta tags updated successfully', {
    ogImage: ogImageUrl,
    twitterImage: twitterImageUrl,
    title: seo.title
  });
};

/**
 * Updates or creates a meta tag
 * @param {string} attribute - Meta tag attribute (name/property)
 * @param {string} value - Meta tag value
 * @param {string} content - Meta tag content
 */
const updateMetaTag = (attribute, value, content) => {
  let meta = document.querySelector(`meta[${attribute}="${value}"]`);
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, value);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
};

/**
 * Updates or creates a link tag
 * @param {string} rel - Link rel attribute
 * @param {string} href - Link href
 */
const updateLinkTag = (rel, href) => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  
  link.href = href;
};

/**
 * Adds structured data (JSON-LD) to the page
 * @param {Object} schema - JSON-LD schema object
 */
const addStructuredData = (schema) => {
  // Remove existing structured data
  const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
  existingScripts.forEach(script => script.remove());

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
};

/**
 * Debug function to check if meta tags are set correctly
 */
export const debugSEO = () => {
  console.log('🔍 SEO Debug Information:');
  console.log('Page Title:', document.title);
  console.log('Meta Description:', document.querySelector('meta[name="description"]')?.content);
  console.log('Canonical URL:', document.querySelector('link[rel="canonical"]')?.href);
  console.log('OG Title:', document.querySelector('meta[property="og:title"]')?.content);
  console.log('OG Description:', document.querySelector('meta[property="og:description"]')?.content);
  console.log('OG Image:', document.querySelector('meta[property="og:image"]')?.content);
  console.log('Twitter Image:', document.querySelector('meta[name="twitter:image"]')?.content);
};

/**
 * Sets default SEO for static pages
 * @param {string} pageName - Name of the page
 */
export const setDefaultSEO = (pageName) => {
  const baseUrl = process.env.REACT_APP_SITE_URL || 'https://jobsopportunity.in';
  
  const defaultSEO = {
    title: `${pageName} - Jobs Opportunity`,
    description: `Jobs Opportunity - Find your dream job. ${pageName} page with career opportunities and job search resources.`,
    canonical: `${baseUrl}/${pageName.toLowerCase()}`,
    og: {
      title: `${pageName} - Jobs Opportunity`,
      description: `Jobs Opportunity - Find your dream job. ${pageName} page with career opportunities and job search resources.`,
      url: `${baseUrl}/${pageName.toLowerCase()}`,
      type: 'website',
      site_name: 'Jobs Opportunity',
      image: `${baseUrl}/og-default.jpg`,
      image_width: '1200',
      image_height: '630'
    },
    twitter: {
      card: 'summary_large_image',
      title: `${pageName} - Jobs Opportunity`,
      description: `Jobs Opportunity - Find your dream job. ${pageName} page with career opportunities and job search resources.`,
      image: `${baseUrl}/og-default.jpg`
    }
  };

  updatePageSEO(defaultSEO);
};